/**
 * Dashboard Page
 *
 * Main landing page showing:
 * - KPI metrics (verticals, products, users, sessions)
 * - Quick links to admin pages (capability-gated)
 * - Recent activity feed
 * - List of verticals in the tenant
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Folder,
  ChevronRight,
  Plus,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Users,
  BarChart2,
  Activity,
  ShieldCheck,
  Layers,
  Package,
  Clock,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";

import { apiRequest } from "../api/client";
import { MetricCard } from "../components/admin/MetricCard";
import { useAdminCapabilities } from "../hooks/useAdminCapabilities";
import { CONFIG_STATUS_INFO } from "../types";

// ============================================================================
// Types
// ============================================================================

interface Vertical {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly description?: string;
  readonly status: "active" | "inactive" | "archived";
  readonly configStatus?: "draft" | "live" | "archived";
  readonly lastModified?: string;
}

interface VerticalsResponse {
  readonly items: readonly Vertical[];
  readonly total: number;
}

interface CreateVerticalForm {
  key: string;
  displayName: string;
  description: string;
  market: string;
  currency: string;
}

interface RecentActivityItem {
  readonly id: string;
  readonly action: string;
  readonly actor: string;
  readonly target: string;
  readonly createdAt: string;
}

interface DashboardData {
  readonly verticalCount: number;
  readonly productCount: number;
  readonly userCount: number;
  readonly sessionCount: number;
  readonly recentActivity: readonly RecentActivityItem[];
}

interface QuickLinkItem {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly href: string;
  readonly icon: ReactElement;
}

// ============================================================================
// Constants
// ============================================================================

const INITIAL_FORM: CreateVerticalForm = {
  key: "",
  displayName: "",
  description: "",
  market: "",
  currency: "",
};

// ============================================================================
// DashboardPage
// ============================================================================

export function DashboardPage(): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [form, setForm] = useState<CreateVerticalForm>(INITIAL_FORM);

  const adminCaps = useAdminCapabilities();

  // Dashboard metrics query — independent of verticals, does not block vertical render
  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const response = await apiRequest<DashboardData>("/api/v1/admin/dashboard");
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    // Non-critical: silently fail without blocking the page
    retry: false,
  });

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["verticals"],
    queryFn: async () => {
      const response = await apiRequest<VerticalsResponse>("/api/v1/verticals");
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (input: CreateVerticalForm) => {
      const body: Record<string, string> = {
        key: input.key,
        displayName: input.displayName,
        market: input.market,
        currency: input.currency,
      };
      if (input.description.trim()) {
        body["description"] = input.description.trim();
      }
      const response = await apiRequest<{ data: { key: string } }>("/api/v1/verticals", {
        method: "POST",
        body,
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["verticals"] });
      setShowCreateDialog(false);
      setForm(INITIAL_FORM);
      navigate(`/studio/${result.data.key}`);
    },
  });

  const handleOpenCreate = useCallback((): void => {
    setForm(INITIAL_FORM);
    setShowCreateDialog(true);
  }, []);

  const handleCloseCreate = useCallback((): void => {
    setShowCreateDialog(false);
    setForm(INITIAL_FORM);
    createMutation.reset();
  }, [createMutation]);

  const handleFormChange = useCallback((field: keyof CreateVerticalForm, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCreateSubmit = useCallback(
    (e: React.FormEvent): void => {
      e.preventDefault();
      createMutation.mutate(form);
    },
    [createMutation, form]
  );

  // Build capability-gated quick links
  const quickLinks: readonly QuickLinkItem[] = [
    ...(adminCaps.canManageTenants
      ? [
          {
            id: "tenants",
            label: "Tenants",
            description: "Manage tenants and onboarding",
            href: "/studio/admin/tenants",
            icon: <Users className="h-5 w-5" />,
          } satisfies QuickLinkItem,
        ]
      : []),
    ...(adminCaps.canViewAudit
      ? [
          {
            id: "audit",
            label: "Audit Logs",
            description: "Review all system activity",
            href: "/studio/admin/audit",
            icon: <ShieldCheck className="h-5 w-5" />,
          } satisfies QuickLinkItem,
          {
            id: "analytics",
            label: "Analytics",
            description: "Click-through and conversion data",
            href: "/studio/admin/analytics",
            icon: <BarChart2 className="h-5 w-5" />,
          } satisfies QuickLinkItem,
        ]
      : []),
    ...(adminCaps.canViewHealth
      ? [
          {
            id: "health",
            label: "System Health",
            description: "Worker and infra health status",
            href: "/studio/admin/health",
            icon: <Activity className="h-5 w-5" />,
          } satisfies QuickLinkItem,
        ]
      : []),
  ];

  return (
    <div className="p-6 space-y-6">
      {/* ================================================================
          KPI Row
          ================================================================ */}
      <section aria-label="Key metrics">
        <h2 className="text-sm font-medium text-foreground-muted mb-3">Overview</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Verticals"
            value={isDashboardLoading ? 0 : (dashboardData?.verticalCount ?? 0)}
            isLoading={isDashboardLoading}
            icon={<Layers className="h-4 w-4" />}
            description="Comparison categories"
          />
          <MetricCard
            label="Products"
            value={isDashboardLoading ? 0 : (dashboardData?.productCount ?? 0)}
            isLoading={isDashboardLoading}
            icon={<Package className="h-4 w-4" />}
            description="Active products"
          />
          <MetricCard
            label="Users"
            value={isDashboardLoading ? 0 : (dashboardData?.userCount ?? 0)}
            isLoading={isDashboardLoading}
            icon={<Users className="h-4 w-4" />}
            description="Tenant members"
          />
          <MetricCard
            label="Active Sessions"
            value={isDashboardLoading ? 0 : (dashboardData?.sessionCount ?? 0)}
            isLoading={isDashboardLoading}
            icon={<Clock className="h-4 w-4" />}
            description="Live sessions"
          />
        </div>
      </section>

      {/* ================================================================
          Quick Links Row (only rendered if any links exist)
          ================================================================ */}
      {quickLinks.length > 0 && (
        <section aria-label="Quick links">
          <h2 className="text-sm font-medium text-foreground-muted mb-3">Admin</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {quickLinks.map((item) => (
              <QuickLinkCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ================================================================
          Recent Activity
          ================================================================ */}
      {!isDashboardLoading && dashboardData && dashboardData.recentActivity.length > 0 && (
        <section aria-label="Recent activity">
          <h2 className="text-sm font-medium text-foreground-muted mb-3">Recent Activity</h2>
          <div className="card divide-y divide-border">
            {dashboardData.recentActivity.map((event) => (
              <RecentActivityRow key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {/* ================================================================
          Verticals section (existing, preserved below)
          ================================================================ */}
      <section>
        {/* Section header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Verticals</h1>
            <p className="mt-1 text-foreground-muted">
              Manage configuration for your comparison verticals
            </p>
          </div>
          <button className="btn btn-primary" onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            New Vertical
          </button>
        </div>

        {/* Create Vertical Dialog */}
        {showCreateDialog && (
          <CreateVerticalDialog
            form={form}
            onChange={handleFormChange}
            onSubmit={handleCreateSubmit}
            onClose={handleCloseCreate}
            isPending={createMutation.isPending}
            error={
              createMutation.isError
                ? createMutation.error instanceof Error
                  ? createMutation.error.message
                  : "Failed to create vertical"
                : undefined
            }
          />
        )}

        {/* Content */}
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : "Failed to load verticals"}
            onRetry={refetch}
          />
        ) : data?.items.length === 0 ? (
          <EmptyState onCreateClick={handleOpenCreate} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data?.items.map((vertical) => (
              <VerticalCard key={vertical.id} vertical={vertical} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// Quick Link Card
// ============================================================================

interface QuickLinkCardProps {
  readonly item: QuickLinkItem;
}

function QuickLinkCard({ item }: QuickLinkCardProps): ReactElement {
  return (
    <Link
      to={item.href}
      className="card flex items-center gap-3 hover:border-primary/50 transition-colors group"
      data-testid={`quick-link-${item.id}`}
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary flex-shrink-0">
        {item.icon}
      </div>
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">{item.label}</p>
        <p className="text-xs text-foreground-muted truncate">{item.description}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-foreground-muted ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </Link>
  );
}

// ============================================================================
// Recent Activity Row
// ============================================================================

interface RecentActivityRowProps {
  readonly event: RecentActivityItem;
}

function RecentActivityRow({ event }: RecentActivityRowProps): ReactElement {
  const formattedDate = new Date(event.createdAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-background-secondary flex-shrink-0">
        <Activity className="h-3.5 w-3.5 text-foreground-muted" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">
          <span className="font-medium">{event.actor}</span>
          <span className="text-foreground-muted mx-1">—</span>
          <span className="text-foreground-muted">{event.action}</span>
        </p>
        <p className="text-xs text-foreground-muted truncate">{event.target}</p>
      </div>
      <span className="text-xs text-foreground-muted flex-shrink-0">{formattedDate}</span>
    </div>
  );
}

// ============================================================================
// Vertical Card
// ============================================================================

interface VerticalCardProps {
  readonly vertical: Vertical;
}

function VerticalCard({ vertical }: VerticalCardProps): ReactElement {
  const statusInfo = vertical.configStatus ? CONFIG_STATUS_INFO[vertical.configStatus] : undefined;

  return (
    <Link
      to={`/studio/${vertical.key}`}
      className="group card flex flex-col hover:border-primary/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Folder className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold truncate">{vertical.name}</h3>
            {statusInfo && (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  statusInfo.color === "warning"
                    ? "bg-warning/10 text-warning"
                    : statusInfo.color === "success"
                      ? "bg-success/10 text-success"
                      : "bg-foreground-muted/10 text-foreground-muted"
                }`}
              >
                {statusInfo.label}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground-muted truncate">
            {vertical.description ?? vertical.key}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-foreground-muted opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      {vertical.lastModified && (
        <p className="mt-3 text-xs text-foreground-muted">
          Last modified: {new Date(vertical.lastModified).toLocaleDateString()}
        </p>
      )}
    </Link>
  );
}

// ============================================================================
// Loading / Error / Empty States
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading verticals...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
        <AlertCircle className="h-6 w-6 text-error" />
      </div>
      <p className="mt-4 text-foreground-muted">{message}</p>
      <button onClick={onRetry} className="btn btn-secondary mt-4">
        <RefreshCw className="mr-2 h-4 w-4" />
        Try again
      </button>
    </div>
  );
}

interface EmptyStateProps {
  readonly onCreateClick: () => void;
}

function EmptyState({ onCreateClick }: EmptyStateProps): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-secondary">
        <Folder className="h-8 w-8 text-foreground-muted" />
      </div>
      <h3 className="mt-4 font-semibold">No verticals yet</h3>
      <p className="mt-2 text-foreground-muted">
        Create your first vertical to get started with configuration.
      </p>
      <button className="btn btn-primary mt-4" onClick={onCreateClick}>
        <Plus className="mr-2 h-4 w-4" />
        Create Vertical
      </button>
    </div>
  );
}

// ============================================================================
// Create Vertical Dialog
// ============================================================================

interface CreateVerticalDialogProps {
  readonly form: CreateVerticalForm;
  readonly onChange: (field: keyof CreateVerticalForm, value: string) => void;
  readonly onSubmit: (e: React.FormEvent) => void;
  readonly onClose: () => void;
  readonly isPending: boolean;
  readonly error?: string | undefined;
}

function CreateVerticalDialog({
  form,
  onChange,
  onSubmit,
  onClose,
  isPending,
  error,
}: CreateVerticalDialogProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-xl border border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Create New Vertical</h2>
          <button onClick={onClose} className="btn btn-ghost p-1" disabled={isPending}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && <div className="mb-4 rounded-md bg-error/10 p-3 text-sm text-error">{error}</div>}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="cv-key" className="block text-sm font-medium mb-1">
              Key
            </label>
            <input
              id="cv-key"
              type="text"
              required
              pattern="^[a-z0-9-]+$"
              placeholder="e.g. health-insurance"
              value={form.key}
              onChange={(e) => onChange("key", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isPending}
            />
            <p className="mt-1 text-xs text-foreground-muted">
              Lowercase alphanumeric with hyphens only
            </p>
          </div>

          <div>
            <label htmlFor="cv-name" className="block text-sm font-medium mb-1">
              Display Name
            </label>
            <input
              id="cv-name"
              type="text"
              required
              placeholder="e.g. Health Insurance"
              value={form.displayName}
              onChange={(e) => onChange("displayName", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isPending}
            />
          </div>

          <div>
            <label htmlFor="cv-desc" className="block text-sm font-medium mb-1">
              Description
            </label>
            <input
              id="cv-desc"
              type="text"
              placeholder="Optional description"
              value={form.description}
              onChange={(e) => onChange("description", e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isPending}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="cv-market" className="block text-sm font-medium mb-1">
                Market
              </label>
              <input
                id="cv-market"
                type="text"
                required
                maxLength={2}
                placeholder="AU"
                value={form.market}
                onChange={(e) => onChange("market", e.target.value.toUpperCase())}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isPending}
              />
            </div>
            <div>
              <label htmlFor="cv-currency" className="block text-sm font-medium mb-1">
                Currency
              </label>
              <input
                id="cv-currency"
                type="text"
                required
                maxLength={3}
                placeholder="AUD"
                value={form.currency}
                onChange={(e) => onChange("currency", e.target.value.toUpperCase())}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={isPending}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={
                isPending || !form.key || !form.displayName || !form.market || !form.currency
              }
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
